import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/auth';
import RebuildSettingsService, {
  type ConnectionEventsResponse,
  type ConnectionsResponse,
  type ConnectionsScope,
  type RebuildSettingsResponse,
} from './rebuild-settings-service';

/**
 * EN-4203: the new system's settings for the signed-in account. `data` stays undefined
 * until the call answers, and when it fails: the caller then shows nothing new.
 */
export function useRebuildSettings() {
  const { currentOrg } = useAuth();

  return useQuery<RebuildSettingsResponse>({
    queryKey: ['rebuild-settings', currentOrg],
    queryFn: () => RebuildSettingsService.get(),
    enabled: !!currentOrg,
    retry: false,
  });
}

/** The new system's connections of the signed-in division, or of the whole account. */
export function useConnections(scope: ConnectionsScope = 'division') {
  const { currentOrg } = useAuth();

  return useQuery<ConnectionsResponse>({
    queryKey: ['settings-connections', currentOrg, scope],
    queryFn: () => RebuildSettingsService.connections(scope),
    enabled: !!currentOrg,
    retry: false,
    staleTime: 60_000,
  });
}

/** One connection's status history, while its dialog is open. */
export function useConnectionEvents(connectionId: string | null) {
  return useQuery<ConnectionEventsResponse>({
    queryKey: ['settings-connection-events', connectionId],
    queryFn: () => RebuildSettingsService.connectionEvents(connectionId ?? ''),
    enabled: !!connectionId,
    retry: false,
  });
}
